import styled from "@emotion/styled";
import { HtmlReactParser, translate } from "@kaltura/mediaspace-shared-utils";
import { Button, Typography } from "@kaltura/ds-react-components";
import { LoginForm, LoginFormProps } from "./LoginForm";
import { CaptchaDisclaimer } from "@kaltura/mediaspace-shared-ui";

interface CallToActionDetails {
    topText: string;
    buttonLabel: string;
    buttonUrl: string;
    bottomText: string;
}

export interface LoginProps extends Omit<LoginFormProps, "onSubmit" | "processing"> {
    callToActionDetails?: CallToActionDetails;
    signInText: string;
    loginText?: string;
}

const LoginMainWrap = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    paddingTop: 38, // with the container's 22px, adds up to 60px
    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
    },
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(1.5),
    },
}));

const LoginMainOuterWrap = styled("div")(({ theme }) => ({
    alignItems: "center",
}));

const LoginFormOuterWrap = styled(
    "div",
    {
        shouldForwardProp: (prop) => prop !== "withCTA",
    }
)<{ withCTA?: boolean }>(({ theme, withCTA = false }) => ({
    flex: "auto",
    ...(
        withCTA && {
            borderLeft: "1px solid #CCCCCC",
            [theme.breakpoints.down("md")]: {
                borderLeft: "none",
                borderTop: "1px solid #CCCCCC",
            },
        }
    ),
}));

const LoginFormWrap = styled(
    "div",
    {
        shouldForwardProp: (prop) => prop !== "inCTA" && prop !== "withCTA",
    }
)<{ inCTA?: boolean; withCTA?: boolean }>(({ theme, inCTA = false, withCTA = false }) => ({
    width: 474,
    margin: "auto",
    padding: 48,
    boxSizing: "border-box",
    [theme.breakpoints.down("sm")]: {
        width: "100%",
    },
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(4),
    },

    ...(
        inCTA && {
            margin: "auto 100px auto auto",
            [theme.breakpoints.up("md")]: {
                [theme.breakpoints.down("lg")]: {
                    margin: "auto auto auto 6px",
                },
            },
            [theme.breakpoints.down("md")]: {
                margin: "auto",
            },
        }
    ),

    ...(
        withCTA && {
            margin: "auto auto auto 100px",
            [theme.breakpoints.up("md")]: {
                [theme.breakpoints.down("lg")]: {
                    margin: "auto 6px auto auto",
                },
            },
            [theme.breakpoints.down("md")]: {
                margin: "auto",
            },
        }
    ),
}));

const CTAScreenElement = styled("div")(({ theme }) => ({
    marginBottom: 24,
    textAlign: "center",
    color: theme.kaltura.palette.tone1,
}));

const Title = styled(Typography)({
    display: "block",
    textAlign: "center",
});

const FullWidthText = styled(Typography)(({ theme }) => ({
    width: "100%",
    textAlign: "center",
    color: theme.kaltura.palette.tone2,
    margin: theme.spacing(3, 0),
    [theme.breakpoints.down("sm")]: {
        width: "100%",
        textAlign: "left",
    },
}));

/**
 * Login page main component.
 * see details regarding form submission in LoginForm.tsx.
 */
export function Login({ callToActionDetails, signInText, loginText, ...loginFormProps }: LoginProps) {
    const { recapthcaMode } = loginFormProps;
    const { topText, buttonLabel, buttonUrl, bottomText } = callToActionDetails || {
        topText: "",
        buttonLabel: "",
        buttonUrl: "",
        bottomText: "",
    };

    return (
        <LoginMainOuterWrap className="kms-ds-login-outer-wrap">
            <LoginMainWrap className="kms-ds-login">
                {!!callToActionDetails && (
                    <LoginFormOuterWrap className={"kms-ds-login-cta-section"}>
                        <LoginFormWrap inCTA={true}>
                            <CTAScreenElement className={"kms-ds-login-cta-top-text"}>
                                <Typography variant={"h2"} component={"h1"}>
                                    {HtmlReactParser(translate(topText))}
                                </Typography>
                            </CTAScreenElement>
                            <CTAScreenElement className={"kms-ds-login-cta-button-wrap"}>
                                <Button
                                    size={"large"}
                                    className={"kms-ds-login-cta-button"}
                                    href={buttonUrl}
                                    target={"_blank"}
                                    rel="noreferrer"
                                >
                                    {translate(buttonLabel)}
                                </Button>
                            </CTAScreenElement>
                            <CTAScreenElement className={"kms-ds-login-cta-bottom-text"}>
                                {HtmlReactParser(translate(bottomText))}
                            </CTAScreenElement>
                        </LoginFormWrap>
                    </LoginFormOuterWrap>
                )}
                <LoginFormOuterWrap className={"kms-ds-login-section"} withCTA={!!callToActionDetails}>
                    <LoginFormWrap withCTA={!!callToActionDetails}>
                        <Title variant={"h2"} component={"h1"} className={"kms-ds-login-title"}>
                            {HtmlReactParser(translate(signInText))}
                        </Title>
                        {loginText && (
                            <FullWidthText className="kms-ds-login-instruction">
                                {HtmlReactParser(translate(loginText))}
                            </FullWidthText>
                        )}
                        <LoginForm {...loginFormProps} />
                    </LoginFormWrap>
                </LoginFormOuterWrap>
            </LoginMainWrap>
            {recapthcaMode && recapthcaMode === "invisible" && <CaptchaDisclaimer />}
        </LoginMainOuterWrap>
    );
}

export default Login;
